/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { FunctionalActor } from '../actors/FunctionalActor';
import { CIRCLE_SHADER, RHOMBUS_SHADER, TRIANGLE_SHADER, CROSS_SHADER, SPHERE_SHADER, LINE_SHADER } from './shaders/ShapeShaders';
import { MeshBasicMaterial, MeshLambertMaterial, MeshDepthMaterial, MeshNormalMaterial, MeshPhongMaterial, MeshPhysicalMaterial, ShaderMaterial, MeshStandardMaterial, PointsMaterial, LineBasicMaterial, Texture, UniformsUtils, } from 'three';
/**
 * helper to create materials
 */
export var MaterialType;
(function (MaterialType) {
    MaterialType["basic"] = "basic";
    MaterialType["depth"] = "depth";
    MaterialType["lambert"] = "lambert";
    MaterialType["normal"] = "normal";
    MaterialType["phong"] = "phong";
    MaterialType["physical"] = "physical";
    MaterialType["shader"] = "shader";
    MaterialType["standard"] = "standard";
    MaterialType["point"] = "point";
    MaterialType["line"] = "line";
    MaterialType["lineshader"] = "lineshader";
})(MaterialType || (MaterialType = {}));
/**
 * available point shader
 */
export var PointsMaterialShape;
(function (PointsMaterialShape) {
    PointsMaterialShape["circle"] = "circle";
    PointsMaterialShape["rhombus"] = "rhombus";
    PointsMaterialShape["triangle"] = "triangle";
    PointsMaterialShape["cross"] = "cross";
    PointsMaterialShape["sphere"] = "sphere";
})(PointsMaterialShape || (PointsMaterialShape = {}));
/**
 * Generators for Material
 */
export class Materials {
    /**
     * creates a new material
     * @param material any structure that describes the material
     * @param scene the scene (to get information of its environment e.g. skybox)
     */
    static create(material, scene) {
        if (material == null) {
            return undefined;
        }
        let result;
        // basic, depth, lambert, normal, phong, standard, physical, shader
        if (scene && material.useBackground && scene.background instanceof Texture) {
            material.params.envMap = scene.background;
        }
        if (scene && material.updater) {
            let func;
            if (typeof (material.updater) === 'string') {
                func = new Function(material.updater);
            }
            else {
                func = material.updater;
            }
            scene.getStage().addActor(new FunctionalActor(func));
        }
        switch (material.type) {
            case MaterialType.basic:
                result = new MeshBasicMaterial(material.params);
                break;
            case MaterialType.depth:
                result = new MeshDepthMaterial(material.params);
                break;
            case MaterialType.lambert:
                result = new MeshLambertMaterial(material.params);
                break;
            case MaterialType.normal:
                result = new MeshNormalMaterial(material.params);
                break;
            case MaterialType.phong:
                result = new MeshPhongMaterial(material.params);
                break;
            case MaterialType.physical:
                result = new MeshPhysicalMaterial(material.params);
                break;
            case MaterialType.shader:
                result = new ShaderMaterial(material.params);
                break;
            case MaterialType.point:
                result = this.createPointMaterial(material);
                break;
            case MaterialType.line:
                result = this.createLineMaterial(material, scene);
                break;
            case MaterialType.lineshader:
                result = Materials._createLineShader();
                break;
            default:
            case MaterialType.standard:
                result = new MeshStandardMaterial(material.params);
                break;
        }
        // this._copyMaterialMembers(result, material);
        return result;
    }
    static createLineMaterial(material, _scene) {
        return new LineBasicMaterial(material.params);
    }
    /**
     * creates a new point material
     * @param material any structure that describes the material
     */
    static createPointMaterial(material) {
        let result;
        if (material != null) {
            switch (material.shape) {
                case PointsMaterialShape.circle:
                    result = this._createShapeShader(CIRCLE_SHADER, material.params);
                    break;
                case PointsMaterialShape.rhombus:
                    result = this._createShapeShader(RHOMBUS_SHADER, material.params);
                    break;
                case PointsMaterialShape.triangle:
                    result = this._createShapeShader(TRIANGLE_SHADER, material.params);
                    break;
                case PointsMaterialShape.cross:
                    result = this._createShapeShader(CROSS_SHADER, material.params);
                    break;
                case PointsMaterialShape.sphere:
                    result = this._createShapeShader(SPHERE_SHADER, material.params);
                    break;
                default:
                    result = new PointsMaterial(material.params);
            }
        }
        else {
            result = new PointsMaterial();
        }
        return result;
    }
    static _createShapeShader(shader, materialParam) {
        const template = {}; // new PointsMaterial(materialParam);
        const prefix = (materialParam.sizeAttenuation) ? '#define USE_SIZEATTENUATION \n' : '';
        template.uniforms = UniformsUtils.clone(shader.uniforms);
        template.fragmentShader = shader.fragmentShader;
        template.vertexShader = prefix + shader.vertexShader;
        const material = new ShaderMaterial(template);
        material.uniforms.size.value = materialParam.size;
        return material;
    }
    static _createLineShader() {
        const template = {}; // new PointsMaterial(materialParam);
        template.uniforms = UniformsUtils.clone(LINE_SHADER.uniforms);
        template.fragmentShader = LINE_SHADER.fragmentShader;
        template.vertexShader = LINE_SHADER.vertexShader;
        const material = new ShaderMaterial(template);
        return material;
    }
    constructor() {
    }
}
//# sourceMappingURL=Materials.js.map